import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverArrow,
    Portal,
    HStack,
    Text,
    Box,
    useMultiStyleConfig,
    useMediaQuery,
} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {InfoIcon} from '../icons'

const InfoPopover = ({
    trigger,
    variant = 'right-center',
    title,
    description,
    showInfoIcon = true,
    popoverModifiers,
    triggerAriaLabel,
}) => {
    const intl = useIntl()
    const style = useMultiStyleConfig('InfoPopover', {variant})
    const infoIconRef = React.useRef(null)
    const [isDesktop] = useMediaQuery('(min-width: 62em)')
    const [popoverOpen, setPopoverOpen] = useState(false)
    const prevIsDesktopRef = React.useRef(isDesktop)

    // Use mobile-specific modifiers with stricter viewport constraints
    const mobileModifiers = [
        {
            name: 'preventOverflow',
            options: {
                padding: 8, // Strict 8px padding on mobile
                tether: false,
            },
        },
        {
            name: 'flip',
            options: {
                fallbackPlacements: ['top', 'bottom', 'right', 'left'],
            },
        },
        {
            name: 'offset',
            options: {
                offset: [0, 8],
            },
        },
    ]

    // On mobile, enforce strict modifiers. On desktop, use custom modifiers from props
    const resolvedModifiers = !isDesktop ? mobileModifiers : popoverModifiers

    // close popover only when *switching* from desktop to mobile
    useEffect(() => {
        if (prevIsDesktopRef.current && !isDesktop && popoverOpen) {
            setPopoverOpen(false)
        }
        prevIsDesktopRef.current = isDesktop
    }, [isDesktop, popoverOpen])

    // close popover when clicking outside on mobile (listener added once on mount)
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                !isDesktop &&
                popoverOpen &&
                infoIconRef.current &&
                !infoIconRef.current.contains(e.target)
            ) {
                setPopoverOpen(false)
            }
        }
        if (!isDesktop) {
            document.addEventListener('click', handleClickOutside)
            return () => document.removeEventListener('click', handleClickOutside)
        }
    }, [isDesktop, popoverOpen])

    return (
        <Popover
            {...style.popover}
            trigger="manual"
            isOpen={popoverOpen}
            onClose={() => setPopoverOpen(false)}
            closeOnBlur={true}
            modifiers={resolvedModifiers}
        >
            <PopoverTrigger>
                <Box
                    ref={infoIconRef}
                    tabIndex={0}
                    role="button"
                    cursor="pointer"
                    aria-haspopup="dialog"
                    aria-label={
                        triggerAriaLabel ||
                        (title
                            ? intl.formatMessage(
                                  {
                                      id: 'info_popover.trigger.aria_label_with_title',
                                      defaultMessage: 'More information: {title}',
                                  },
                                  {title},
                              )
                            : intl.formatMessage({
                                  id: 'info_popover.trigger.aria_label_generic',
                                  defaultMessage: 'More information',
                              }))
                    }
                    onMouseEnter={() => {
                        if (isDesktop) setPopoverOpen(true)
                    }}
                    onMouseLeave={() => {
                        if (isDesktop) setPopoverOpen(false)
                    }}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (!isDesktop) {
                            setPopoverOpen(!popoverOpen)
                        }
                    }}
                    onKeyDown={(e) => {
                        // hover-only on desktop would lock out keyboard users, so keys work everywhere
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            setPopoverOpen(!popoverOpen)
                        } else if (e.key === 'Escape' && popoverOpen) {
                            e.stopPropagation()
                            setPopoverOpen(false)
                        }
                    }}
                    display="flex"
                >
                    {trigger}
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    {...style.container}
                    sx={{
                        maxWidth: 'min(18rem, calc(100vw - 1.5rem))',
                    }}
                    data-testid="popover-content"
                    role="dialog"
                    aria-label={
                        title ||
                        intl.formatMessage({
                            id: 'global.info.popover.title.label',
                            defaultMessage: 'Information',
                        })
                    }
                >
                    <PopoverArrow {...style.arrow} />
                    <PopoverBody {...style.body}>
                        <HStack {...style.hstack}>
                            {showInfoIcon && (
                                <InfoIcon
                                    {...style.infoIcon}
                                    data-testid="info-icon"
                                    cursor="default"
                                    aria-hidden="true"
                                    focusable="false"
                                    role="presentation"
                                />
                            )}

                            <Box {...style.texts}>
                                {title !== null && title !== undefined && (
                                    <Text {...style.title}>{title}</Text>
                                )}

                                <Text {...style.description}>
                                    {description ||
                                        intl.formatMessage({
                                            id: 'global.info.popover.description',
                                            defaultMessage: 'Lorem ipsum dolor sit amet',
                                        })}
                                </Text>
                            </Box>
                        </HStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    )
}

InfoPopover.propTypes = {
    variant: PropTypes.string,
    trigger: PropTypes.node.isRequired,
    placement: PropTypes.string,
    alignInfoIcon: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    showInfoIcon: PropTypes.bool,
    popoverModifiers: PropTypes.array,
    triggerAriaLabel: PropTypes.string,
}

export default InfoPopover
